import React, { useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import { useSocket } from "./Contexts/SocketContext";
import { useStreamInput } from "../hooks/useStreamInput";
import {
  BiWifi,
  BiWifi2,
  BiWifi1,
  BiWifiOff,
  BiSignal5,
  BiSignal3,
  BiSignal1,
  BiNetworkChart,
} from "react-icons/bi";

interface LiveStreamModalProps {
  systemId: number;
  systemName: string;
  patientName: string;
  networkType: string;
  ping: number;
  organizationId: string;
  onClose: () => void;
}

const MAX_DURATION = 2 * 60 * 60; // 2 hours in seconds
const STUN_SERVERS = [{ urls: "stun:turn.epicarealert.com:3478" }];

const LiveStreamModal: React.FC<LiveStreamModalProps> = ({
  systemId,
  systemName,
  patientName,
  networkType,
  ping,
  organizationId,
  onClose,
}) => {
  const getConnectionIcon = () => {
    const color = ping < 150 ? "#10b981" : ping < 300 ? "#f59e0b" : "#ef4444";
    if (networkType === "cellular") {
      if (ping < 150) return <BiSignal5 style={{ color }} />;
      if (ping < 300) return <BiSignal3 style={{ color }} />;
      return <BiSignal1 style={{ color }} />;
    }
    if (networkType === "ethernet") return <BiNetworkChart style={{ color }} />;
    if (ping < 150) return <BiWifi style={{ color }} />;
    if (ping < 300) return <BiWifi2 style={{ color }} />;
    if (ping < 500) return <BiWifi1 style={{ color }} />;
    return <BiWifiOff style={{ color: "#ef4444" }} />;
  };
  const { socket } = useSocket();
  const videoRef = useRef<HTMLVideoElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [connected, setConnected] = useState(false);
  const [dots, setDots] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Live WebRTC sender stats from the system (refreshed ~every 2s while
  // streaming). Used to render the small overlay in the header — bitrate,
  // actual fps, encoder-reported quality limitation, etc. Per platform
  // owner this is only visible inside the live view modal, not on system
  // cards on the dashboard.
  interface StreamStats {
    bitrate_kbps: number;
    fps: number;
    width: number;
    height: number;
    quality_reason: "none" | "bandwidth" | "cpu" | "other" | string;
    available_kbps: number;
    rtc_rtt_ms: number;
    preset?: string;
  }
  const [streamStats, setStreamStats] = useState<StreamStats | null>(null);
  // Local cursor: we set the video element's CSS `cursor` to a custom
  // arrow SVG via data: URL. The browser draws the cursor itself, at the
  // OS-reported mouse position, with no React or overlay math involved —
  // so the cursor feels instant at any network latency and there's no
  // chance of pixel drift between where the operator clicks and where the
  // cursor is drawn. Cursor-tracking + emit logic lives in useStreamInput.
  // High-ping systems go through a backend-driven warmup gate before any
  // ICE/DTLS happens. The operator should see "warming up" rather than the
  // generic "connecting" during that phase so they know we're working on
  // it, not stuck. Default-true when ping > 500 (backend will warmup);
  // flipped to false the moment TURN_CREDENTIALS arrives (warmup succeeded,
  // we're moving on to actual WebRTC negotiation).
  const [isWarmingUp, setIsWarmingUp] = useState(ping > 500);
  const offerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const iceServersRef = useRef<RTCIceServer[]>(STUN_SERVERS);
  // Backend flips this on when the system's tracked ping is high enough that
  // P2P ICE won't finish before the operator gives up. When true we skip
  // host/srflx candidates and go straight through TURN over TCP/443.
  const forceRelayRef = useRef(false);
  const videoWrapperRef = useRef<HTMLDivElement>(null);

  // Input pipeline lives in the shared hook so MultiViewModal's focused
  // pane can reuse it. enabled=true here — LiveStreamModal is always in
  // control of the system it's viewing.
  const {
    handleMouseMove,
    handleMouseDown,
    handleMouseUp,
    handleKeyDown,
    bindInputChannel,
    inputOverDC,
  } = useStreamInput({
    socket,
    systemId,
    videoRef,
    videoWrapperRef,
    enabled: true,
  });

  // Note-injection popup: operator can write a note to the EEG study without
  // leaving the live view. Uses the existing writenote.exe pipeline that the
  // run-check flow already uses — wave.exe opens its Note Details dialog
  // cross-process and we fill+commit it. Operator never has to type into the
  // remote system or break their flow.
  //
  // We wait for `wn_done` from the system before closing the popup so the
  // operator gets real success/failure feedback. An 8s hard timeout caps the
  // wait if writenote.exe hangs or the system never replies — the popup
  // closes anyway so the operator isn't stuck.
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [notePending, setNotePending] = useState(false);
  const noteTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const submitNote = () => {
    if (!socket) return;
    setNotePending(true);
    socket.emit("wn", { systemId, note: noteText.trim() });
    if (noteTimeoutRef.current) clearTimeout(noteTimeoutRef.current);
    noteTimeoutRef.current = setTimeout(() => {
      setNotePending(false);
      setShowNotePopup(false);
      setNoteText("");
    }, 8000);
  };
  // Manual ping-bucket bitrate throttle removed — WebRTC's native congestion
  // control adapts bitrate to real packet loss / RTT trends, which works
  // better than our static "ping > 500 → 50 Kbps / 3 fps" cliff (that made
  // high-latency hospital connections look completely broken even when the
  // underlying link was usable). Native adaptation handles it.

  const cleanup = () => {
    if (offerTimeoutRef.current) clearTimeout(offerTimeoutRef.current);
    bindInputChannel(null);
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    if (socket) socket.emit("STOP_STREAM", { systemId });
    onClose();
  };

  const retry = () => {
    if (offerTimeoutRef.current) clearTimeout(offerTimeoutRef.current);
    if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
    setError(null);
    setConnected(false);
    setElapsed(0);
    setStreamStats(null);
    setIsWarmingUp(ping > 500);
    if (socket) {
      socket.off("STREAM_OFFER");
      socket.off("STREAM_ICE_FROM_SYSTEM");
      socket.emit("REQUEST_STREAM", { systemId, organizationId });
      setupSocketListeners();
    }
  };

  useEffect(() => {
    if (connected) return;
    const interval = setInterval(() => {
      setDots((d) => (d.length >= 3 ? "" : d + "."));
    }, 500);
    return () => clearInterval(interval);
  }, [connected]);

  const setupSocketListeners = () => {
    if (!socket) return;

    // Tiered timeout — high-ping systems go through a warmup gate where
    // backend waits up to 30s for ping to drop below the connect threshold
    // before kicking off WebRTC negotiation. Add headroom for the actual
    // handshake on top of that. Healthy fast systems still snap-fail at 10s.
    // This guards OFFER ARRIVAL only — it's cleared the moment STREAM_OFFER
    // lands (below). After that there's no connection deadline; we wait
    // for ICE indefinitely so a very slow link gets unlimited time to
    // establish over TURN. The tiers just give a slow warmup + offer
    // round-trip enough headroom before we declare the system unreachable.
    // Very high ping (≥800ms, e.g. saturated cellular) gets the longest
    // leash — there's no hard latency wall, TURN-over-TCP works at
    // satellite RTTs, it just needs patience.
    const offerTimeout =
      ping > 800 ? 90000 : ping > 500 ? 60000 : ping > 300 ? 30000 : 10000;
    offerTimeoutRef.current = setTimeout(() => {
      setError("System did not respond. It may be offline or restarting.");
    }, offerTimeout);

    socket.on("STREAM_OFFER", async ({ offer }) => {
      if (offerTimeoutRef.current) clearTimeout(offerTimeoutRef.current);

      if (pcRef.current) { pcRef.current.close(); pcRef.current = null; }
      setConnected(false);
      setError(null);

      const pcConfig: RTCConfiguration = { iceServers: iceServersRef.current };
      if (forceRelayRef.current) pcConfig.iceTransportPolicy = "relay";
      const pc = new RTCPeerConnection(pcConfig);
      pcRef.current = pc;

      pc.ontrack = ({ streams }) => {
        if (videoRef.current && streams[0]) {
          videoRef.current.srcObject = streams[0];
          setConnected(true);
          setTimeout(() => videoWrapperRef.current?.focus(), 100);
        }
      };

      // System creates a data channel for input events; hand it off to the
      // input hook, which owns channel state + open/close transitions.
      pc.ondatachannel = (event) => {
        if (event.channel.label !== "input") return;
        bindInputChannel(event.channel);
      };

      pc.onicecandidate = ({ candidate }) => {
        if (candidate) {
          socket.emit("STREAM_ICE_FROM_FRONTEND", { candidate: candidate.toJSON(), systemId });
        }
      };

      pc.oniceconnectionstatechange = () => {
        const state = pc.iceConnectionState;
        if (state === "failed") {
          setError("Connection failed. Your network may block peer-to-peer traffic.");
          setConnected(false);
        }
      };

      try {
        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        socket.emit("STREAM_ANSWER", { answer: { type: answer.type, sdp: answer.sdp }, systemId });
      } catch (e) {
        setError("Failed to establish stream. Please try again.");
      }
    });

    socket.on("STREAM_ICE_FROM_SYSTEM", async ({ candidate }) => {
      if (pcRef.current) {
        try {
          await pcRef.current.addIceCandidate(new RTCIceCandidate(candidate));
        } catch (_) {}
      }
    });
  };

  useEffect(() => {
    if (!socket) return;
    socket.on("TURN_CREDENTIALS", ({ username, credential, forceRelay, local }) => {
      forceRelayRef.current = !!forceRelay;
      // TURN_CREDENTIALS only arrives after backend's warmup gate has
      // passed (ping dropped below the connect threshold). Flip the UI
      // from "warming up" to the generic "connecting" spinner.
      setIsWarmingUp(false);
      // `local` means the other end lives in this same browser (the sandbox's
      // simulated workstation): no STUN or TURN is needed, and contacting the
      // TURN server with placeholder credentials would only slow things down.
      // The real backend never sets it.
      if (local) {
        iceServersRef.current = [];
        return;
      }
      iceServersRef.current = [
        ...STUN_SERVERS,
        { urls: "turns:turn.epicarealert.com:443?transport=tcp", username, credential },
        { urls: "turns:turn.epicarealert.com:5349?transport=tcp", username, credential },
        { urls: "turn:turn.epicarealert.com:3478?transport=tcp", username, credential },
        { urls: "turn:turn.epicarealert.com:3478", username, credential },
      ];
    });

    // System reports note injection outcome. Close popup either way (success
    // or failure) — operator can retry on the remote screen if needed. The
    // 8s safety timeout in submitNote handles the case where this never fires.
    socket.on("wn_done", () => {
      if (noteTimeoutRef.current) {
        clearTimeout(noteTimeoutRef.current);
        noteTimeoutRef.current = null;
      }
      setNotePending(false);
      setShowNotePopup(false);
      setNoteText("");
    });

    // Live encoder/transport stats from the sender. Backend scopes the emit
    // to whichever socket is the active viewer, but we still filter by
    // systemId so a stale event from a previous session can't poison the
    // overlay if the operator re-opens the modal quickly.
    socket.on("STREAM_STATS", (payload) => {
      if (payload && String(payload.systemId) === String(systemId)) {
        setStreamStats(payload as StreamStats);
      }
    });

    // Backend bailed before sending TURN credentials — usually because the
    // system's pre-stream warmup burst couldn't get back through its
    // network in time, which is a strong signal the link is too saturated
    // for WebRTC. Show the specific reason instead of letting the generic
    // 30s offer timeout fire with a "did not respond" message.
    socket.on("STREAM_REJECTED", (payload) => {
      if (!payload || String(payload.systemId) !== String(systemId)) return;
      if (offerTimeoutRef.current) {
        clearTimeout(offerTimeoutRef.current);
        offerTimeoutRef.current = null;
      }
      setError(payload.message || "Live view could not be started.");
      setConnected(false);
    });

    socket.emit("REQUEST_STREAM", { systemId, organizationId });
    setupSocketListeners();

    return () => {
      if (offerTimeoutRef.current) clearTimeout(offerTimeoutRef.current);
      if (noteTimeoutRef.current) clearTimeout(noteTimeoutRef.current);
      socket.off("TURN_CREDENTIALS");
      socket.off("STREAM_OFFER");
      socket.off("STREAM_ICE_FROM_SYSTEM");
      socket.off("STREAM_STATS");
      socket.off("STREAM_REJECTED");
      socket.off("wn_done");
    };
  }, [socket, systemId]);

  // Clipboard sync — pull from remote and write to local
  useEffect(() => {
    if (!socket) return;

    let lastReceived = "";
    const onClipboardFromRemote = ({ text }: { text: string }) => {
      if (typeof text !== "string" || text === lastReceived) return;
      lastReceived = text;
      navigator.clipboard.writeText(text).catch(() => {
        // Permissions may require focus; silently ignore on failures
      });
    };
    socket.on("CLIPBOARD_DATA", onClipboardFromRemote);

    // Push local clipboard to remote when modal regains focus (user copied
    // something locally and is now coming back to the live view).
    const pushLocalClipboard = async () => {
      try {
        const text = await navigator.clipboard.readText();
        if (text && text !== lastReceived) {
          lastReceived = text;
          socket.emit("CLIPBOARD_FROM_USER", { text, systemId });
        }
      } catch (_) {}
    };
    window.addEventListener("focus", pushLocalClipboard);

    return () => {
      socket.off("CLIPBOARD_DATA", onClipboardFromRemote);
      window.removeEventListener("focus", pushLocalClipboard);
    };
  }, [socket, systemId]);

  // Stall watchdog. `connected` flips when the remote track is attached,
  // which happens as soon as the offer is accepted — before ICE has actually
  // found a path. On a slow network that can take well over the 4 seconds
  // this used to allow, so a connection that was still being set up was
  // reported as "lost". Now the stall check only starts once the first
  // frame has played; a stream that never starts gets a separate, longer
  // deadline.
  useEffect(() => {
    if (!connected || !videoRef.current) return;
    const video = videoRef.current;
    const NEVER_STARTED_MS = 30000;
    const startedAt = Date.now();
    let lastTime = video.currentTime;
    let started = lastTime > 0;
    let stalls = 0;
    const interval = setInterval(() => {
      const now = video.currentTime;
      if (!started) {
        if (now > 0) {
          started = true;
        } else if (Date.now() - startedAt > NEVER_STARTED_MS) {
          setError("Video did not start. The network may block the connection — try reconnecting.");
          setConnected(false);
        }
        lastTime = now;
        return;
      }
      if (now === lastTime) {
        stalls++;
        if (stalls >= 2) {
          setError("Video stream lost. The network may have changed — try reconnecting.");
          setConnected(false);
        }
      } else {
        stalls = 0;
      }
      lastTime = now;
    }, 2000);
    return () => clearInterval(interval);
  }, [connected]);

  const formatElapsed = (s: number) => {
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const sec = s % 60;
    if (m < 60) return `${m}m ${sec.toString().padStart(2, "0")}s`;
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${h}h ${min.toString().padStart(2, "0")}m`;
  };

  useEffect(() => {
    if (!connected) return;
    const interval = setInterval(() => {
      setElapsed((e) => {
        if (e + 1 >= MAX_DURATION) { clearInterval(interval); cleanup(); return e + 1; }
        return e + 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [connected]);

  return (
    <Modal
      isOpen
      onRequestClose={cleanup}
      className="custom-modal"
      overlayClassName="custom-overlay"
      style={{
        content: {
          width: "82%",
          height: "85vh",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          padding: 0,
          border: "none",
          borderRadius: "14px",
          overflow: "hidden",
          background: "#111",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          height: "100%",
          padding: "16px",
          background: "#111",
        }}
        onKeyDown={(e) => { if (e.key === "Tab") { e.preventDefault(); e.stopPropagation(); } }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
            <span
              style={{
                color: "#94a3b8",
                fontSize: "11px",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              {patientName}
            </span>
            <span style={{ color: "white", fontWeight: 600, fontSize: "15px" }}>
              {systemName} — Live View
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {connected && (
              <span style={{
                color: elapsed >= MAX_DURATION - 600 ? "#ef4444" : "#94a3b8",
                fontWeight: 500,
                fontSize: "13px",
                fontVariantNumeric: "tabular-nums",
              }}>
                {formatElapsed(elapsed)}
              </span>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "white", fontSize: "18px" }}>
              {getConnectionIcon()}
              {ping > 0 && (
                <span style={{ color: "#94a3b8", fontSize: "12px", fontWeight: 500 }}>{ping}ms</span>
              )}
              {connected && ping > 0 && (
                <span style={{
                  fontSize: "10px", fontWeight: 700, padding: "2px 6px", borderRadius: "6px",
                  background: ping < 150 ? "rgba(16,185,129,0.2)" : ping < 300 ? "rgba(245,158,11,0.2)" : "rgba(239,68,68,0.2)",
                  color: ping < 150 ? "#10b981" : ping < 300 ? "#f59e0b" : "#ef4444",
                }}>
                  {ping < 150 ? "HD" : ping < 300 ? "MED" : ping < 500 ? "LOW" : "POOR"}
                </span>
              )}
              {connected && (
                <span
                  title={inputOverDC ? "Input is direct peer-to-peer (lower latency)" : "Input is relayed through the server"}
                  style={{
                    fontSize: "10px", fontWeight: 700, padding: "2px 6px", borderRadius: "6px",
                    background: inputOverDC ? "rgba(124,58,237,0.2)" : "rgba(100,116,139,0.2)",
                    color: inputOverDC ? "#a78bfa" : "#cbd5e1",
                  }}
                >
                  {inputOverDC ? "P2P" : "WS"}
                </span>
              )}
              {connected && streamStats && (() => {
                // Color the dot from `qualityLimitationReason` — the single
                // best "why is the stream degraded" signal the WebRTC stack
                // exposes. Bandwidth-limited is the common case on slow
                // hospital networks; CPU-limited means the system is too busy
                // to encode (rare, but a useful tell for the platform owner).
                const reason = streamStats.quality_reason;
                const dotColor =
                  reason === "none" ? "#10b981"
                  : reason === "bandwidth" ? "#f59e0b"
                  : reason === "cpu" ? "#ef4444"
                  : "#94a3b8";
                const tooltip = [
                  streamStats.bitrate_kbps > 0 ? `Sending ${streamStats.bitrate_kbps} kbps` : null,
                  streamStats.available_kbps > 0 ? `Network capacity ~${streamStats.available_kbps} kbps` : null,
                  streamStats.fps > 0 ? `${streamStats.fps} fps` : null,
                  streamStats.width > 0 ? `${streamStats.width}×${streamStats.height}` : null,
                  streamStats.rtc_rtt_ms > 0 ? `Stream RTT ${streamStats.rtc_rtt_ms}ms` : null,
                  reason !== "none" ? `Limited by: ${reason}` : "No quality limit",
                  streamStats.preset ? `Preset: ${streamStats.preset}` : null,
                ].filter(Boolean).join("\n");
                return (
                  <span
                    title={tooltip}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      fontSize: "10px",
                      fontWeight: 700,
                      padding: "2px 8px",
                      borderRadius: "6px",
                      background: "rgba(15,23,42,0.5)",
                      color: "#cbd5e1",
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <span style={{
                      width: 6, height: 6, borderRadius: "50%",
                      background: dotColor,
                      boxShadow: `0 0 6px ${dotColor}`,
                    }} />
                    {streamStats.bitrate_kbps > 0 ? `${streamStats.bitrate_kbps} kbps` : "—"}
                    {streamStats.fps > 0 ? ` · ${streamStats.fps} fps` : ""}
                    {streamStats.width > 0 ? ` · ${streamStats.width}×${streamStats.height}` : ""}
                  </span>
                );
              })()}
            </div>
            {(() => {
              // Add Note requires an active study — writenote.exe drives the
              // NeuroWorks "Note Details" dialog, which only exists when a
              // study is open. Without a patient, the button is dead weight.
              const hasStudy = !!patientName?.trim();
              const canAddNote = connected && hasStudy;
              const title = !connected
                ? "Connect to the live stream first"
                : !hasStudy
                  ? "No active study on this system"
                  : "Add a note to the EEG study without leaving live view";
              return (
                <button
                  onClick={() => setShowNotePopup(true)}
                  disabled={!canAddNote}
                  title={title}
                  style={{
                    background: canAddNote ? "#2563eb" : "#1e3a8a",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    padding: "6px 14px",
                    cursor: canAddNote ? "pointer" : "not-allowed",
                    fontWeight: 600,
                    fontSize: "13px",
                    marginRight: "8px",
                    opacity: canAddNote ? 1 : 0.6,
                  }}
                >
                  Add Note
                </button>
              );
            })()}
            {/* Ctrl+Alt+Delete button disabled for now. Wiring (sas.exe,
                SEND_SAS socket event, SoftwareSASGeneration registry write)
                is all still in place — just flip SAS_BUTTON_ENABLED to true
                to bring it back. */}
            {false && (
              <button
                onClick={() => {
                  if (socket) socket.emit("SEND_SAS", { systemId });
                }}
                disabled={!connected}
                title="Sends Ctrl+Alt+Del to the remote system (the operator's local OS would otherwise intercept the keystroke)"
                style={{
                  background: connected ? "#475569" : "#334155",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  padding: "6px 14px",
                  cursor: connected ? "pointer" : "not-allowed",
                  fontWeight: 600,
                  fontSize: "13px",
                  marginRight: "8px",
                  opacity: connected ? 1 : 0.6,
                }}
              >
                Send Ctrl+Alt+Delete
              </button>
            )}
            <button
              onClick={cleanup}
              style={{
                background: "#ef4444",
                border: "none",
                borderRadius: "8px",
                color: "white",
                padding: "6px 14px",
                cursor: "pointer",
                fontWeight: 600,
                fontSize: "13px",
              }}
            >
              Stop
            </button>
          </div>
        </div>

        {/* Note popup — overlays the video. Click outside or Cancel to dismiss. */}
        {showNotePopup && (
          <div
            onClick={() => !notePending && setShowNotePopup(false)}
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(15, 23, 42, 0.65)",
              backdropFilter: "blur(4px)",
              zIndex: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "#1e293b",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "14px",
                padding: "22px",
                width: "min(520px, 90%)",
                boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
              }}
            >
              <div style={{ color: "#f8fafc", fontSize: "16px", fontWeight: 600, marginBottom: "4px" }}>
                Add note to study
              </div>
              <div style={{ color: "#94a3b8", fontSize: "12px", marginBottom: "14px" }}>
                Inserted into NeuroWorks at the current EEG position. Leave blank for "Checked by [your name]".
              </div>
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation(); // don't forward to remote
                  if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submitNote();
                  if (e.key === "Escape" && !notePending) setShowNotePopup(false);
                }}
                autoFocus
                placeholder="Type the note... (Ctrl+Enter to submit)"
                disabled={notePending}
                style={{
                  width: "100%",
                  minHeight: "90px",
                  background: "#0f172a",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "8px",
                  padding: "10px 12px",
                  color: "#f8fafc",
                  fontSize: "13px",
                  fontFamily: "inherit",
                  resize: "vertical",
                  outline: "none",
                  opacity: notePending ? 0.6 : 1,
                }}
              />
              <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "14px" }}>
                <button
                  onClick={() => setShowNotePopup(false)}
                  disabled={notePending}
                  style={{
                    background: "transparent",
                    border: "1px solid rgba(255,255,255,0.15)",
                    borderRadius: "8px",
                    color: "#cbd5e1",
                    padding: "8px 16px",
                    cursor: notePending ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: "13px",
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitNote}
                  disabled={notePending}
                  style={{
                    background: "#2563eb",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    padding: "8px 18px",
                    cursor: notePending ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    fontSize: "13px",
                    opacity: notePending ? 0.6 : 1,
                  }}
                >
                  {notePending ? "Adding..." : "Add Note"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Video area — always full size */}
        <div
          ref={videoWrapperRef}
          tabIndex={0}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onContextMenu={(e) => e.preventDefault()}
          onKeyDown={handleKeyDown}
          style={{
            flex: 1,
            position: "relative",
            borderRadius: "10px",
            overflow: "hidden",
            background: "#000",
            width: "100%",
            outline: "none",
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            onMouseMove={handleMouseMove}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: connected ? "block" : "none",
              // Custom local cursor — browser draws this at the actual mouse
              // position. The hotspot "1 1" tells the browser the click point
              // is at pixel (1, 1) of the image, which is the tip of the arrow.
              // Result: cursor moves at hand speed (zero network latency) and
              // is positioned by the browser, not by React, so it can't drift.
              cursor:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='24' viewBox='0 0 20 24'><path d='M1 1 L1 17 L5 13 L8 20 L10 19 L7 12 L13 12 Z' fill='white' stroke='black' stroke-width='1.3' stroke-linejoin='round'/></svg>\") 1 1, auto",
            }}
          />

          {!connected && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "16px" }}>
              {error ? (
                <>
                  <div style={{ fontSize: "32px" }}>⚠️</div>
                  <span style={{ color: "#f87171", fontSize: "14px", fontWeight: 500, textAlign: "center", maxWidth: "320px", lineHeight: 1.5 }}>
                    {error}
                  </span>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button
                      onClick={retry}
                      style={{ background: "#7c3aed", border: "none", borderRadius: "8px", color: "white", padding: "8px 20px", cursor: "pointer", fontWeight: 600, fontSize: "13px" }}
                    >
                      Retry
                    </button>
                    <button
                      onClick={cleanup}
                      style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: "8px", color: "#94a3b8", padding: "8px 20px", cursor: "pointer", fontWeight: 600, fontSize: "13px" }}
                    >
                      Close
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ width: "48px", height: "48px", borderRadius: "50%", border: "3px solid rgba(255,255,255,0.1)", borderTopColor: "#7c3aed", animation: "spin 0.9s linear infinite" }} />
                  <span style={{ color: "#94a3b8", fontSize: "15px", fontWeight: 500, letterSpacing: "0.02em", minWidth: "110px", textAlign: "center" }}>
                    {isWarmingUp ? `Warming up the system${dots}` : `Connecting${dots}`}
                  </span>
                  {isWarmingUp && (
                    <span style={{ color: "#64748b", fontSize: "12px", fontWeight: 400, textAlign: "center", maxWidth: "320px", lineHeight: 1.5 }}>
                      This system's network is slow — please wait while we prepare the connection. This can take up to 30 seconds.
                    </span>
                  )}
                </>
              )}
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default LiveStreamModal;
